import { useCallback, useMemo } from 'react';

import { Flex, Button, IconExperimentLine, Text, PrimaryColors, Colors } from '@revenuemarket/cds';
import { overlays } from '@revenuemarket/overlays';
import { fixedNumFormat } from '@revenuemarket/utils/number/formatNum';
import { useRouter } from 'next/router';

import { signOut } from '~apis/auth/signin';
import ScrapeStatusDrawer from '~components/ui/layout/ScrapeStatusDrawer';
import checkIsOnboardingUser from '~hooks/checkIsOnboardingUser';
import { useGetOnboardingStatus, useGetSettings } from '~hooks/queries/setting/query';
import useIsDemoTrial from '~hooks/useIsDemoTrial';
import { css } from '~libs/emotion';

import useGetAnalysisResult from './useGetAnalysisResult';
import CertificateRegisterModal from '../Modal/CertificateRegisterModal';

const BANNER_HEIGHT = 44;
const BANNER_GAP = 8;
const BANNER_MARGIN = 16;

const useGetAlertBannerInfo = () => {
  const router = useRouter();
  const isDemoTrial = useIsDemoTrial();
  const {
    organizationsInfo,
    bankRegistered,
    homeTaxRegistered,
    isAnalysisInprogress,
    percent,
    taxInvoicePercent,
  } = useGetAnalysisResult();
  const { data } = useGetOnboardingStatus();
  const { data: settings } = useGetSettings();
  const labelRatio = useMemo(() => data?.Data?.data?.labelStats?.labeledRatio ?? 0, [data]);
  const pendingMandateRequest = useMemo(
    () => settings?.Data?.data?.pendingMandateRequest,
    [settings],
  );
  const isOnboardingUser = checkIsOnboardingUser(settings?.Data?.data?.onboardingStep);

  const isShowAlertBanner = useMemo(
    () => isDemoTrial || !bankRegistered || !homeTaxRegistered || isAnalysisInprogress,
    [isDemoTrial, bankRegistered, homeTaxRegistered, isAnalysisInprogress],
  );
  const isExistBanner = useMemo(
    () => isShowAlertBanner || pendingMandateRequest,
    [isShowAlertBanner, pendingMandateRequest],
  );
  const bannerHeight = useMemo(
    () =>
      isShowAlertBanner && pendingMandateRequest
        ? BANNER_HEIGHT * 2 + BANNER_GAP + BANNER_MARGIN
        : isShowAlertBanner || pendingMandateRequest
          ? BANNER_HEIGHT + BANNER_MARGIN
          : 0,
    [isShowAlertBanner, pendingMandateRequest],
  );

  const getAlertInfo = useCallback(() => {
    if (isDemoTrial) {
      return {
        type: 'warning',
        icon: <IconExperimentLine fill="#FAAD14" />,
        description: (
          <Flex align="center" gap={8}>
            <Text textStyle="14_400">데모 데이터를 체험하고 있습니다.</Text>
            <RegisterTriggerText text="가입하고 클로브 사용하기" />
          </Flex>
        ),
        onClick: () => {
          signOut().then(() => {
            router.push('/auth/signup');
          });
        },
      };
    }

    // 둘 다 연동 안되어있을 때
    if (!bankRegistered && !homeTaxRegistered) {
      return {
        type: 'warning',
        icon: <IconExperimentLine fill="#FAAD14" />,
        description: (
          <Flex align="center" gap={8}>
            <Text textStyle="14_400">
              데모 데이터를 체험하고 있습니다. 수정한 내용은 저장되지 않습니다.
            </Text>
            <RegisterTriggerText text="실제 데이터 연동하기" />
          </Flex>
        ),
        onClick: () => {
          overlays.open(({ isOpen, onClose }) => (
            <CertificateRegisterModal open={isOpen} onCancel={onClose} />
          ));
        },
      };
    }

    if (isOnboardingUser) {
      // 온보딩 유저는 ScrapeFloatingPanel에서 처리
      return null;
    }

    // 은행만 연동 되어있을 때
    if (bankRegistered && !homeTaxRegistered) {
      return {
        type: 'warning',
        icon: <IconExperimentLine fill="#FAAD14" />,
        description: (
          <Flex align="center" gap={8}>
            <Text textStyle="14_400">
              은행 거래내역 중 {fixedNumFormat(labelRatio * 100, 0)}%만 자동 분류되었어요. 홈택스
              공동인증서를 연동해서 모든 기능을 활용해보세요
            </Text>
            <RegisterTriggerText />
          </Flex>
        ),
        onClick: () => {
          overlays.open(({ isOpen, onClose }) => (
            <CertificateRegisterModal open={isOpen} onCancel={onClose} />
          ));
        },
      };
    }

    // 홈택스만 되어있을 때
    if (!bankRegistered && homeTaxRegistered) {
      return {
        type: 'warning',
        icon: <IconExperimentLine fill="#FAAD14" />,
        description: (
          <Flex justify="space-between" align="center">
            <Flex align="center" gap={8}>
              {' '}
              <Text textStyle="14_400">은행 공동인증서를 연동해서 모든 기능을 활용해보세요.</Text>
              <RegisterTriggerText />
            </Flex>
            <Text textStyle="14_400">홈택스 데이터를 불러오고 있어요... {taxInvoicePercent}%</Text>
          </Flex>
        ),
        onClick: () => {
          overlays.open(({ isOpen, onClose }) => (
            <CertificateRegisterModal open={isOpen} onCancel={onClose} />
          ));
        },
      };
    }

    // 분석중일때
    if (isAnalysisInprogress) {
      return {
        type: 'info',
        description: (
          <Flex
            align="center"
            gap={8}
            onClick={() => {
              overlays.open(({ isOpen, onClose }) => (
                <ScrapeStatusDrawer isOpen={isOpen} onClose={onClose} />
              ));
            }}
          >
            <Flex>
              <Text
                style={{
                  fontSize: 14,
                }}
              >
                <Text textStyle="14_400">최신 데이터를 불러오고 있어요. {percent}%</Text>
              </Text>
            </Flex>
            <Flex align="center">
              <Button buttonType="weak" buttonSize="small">
                수집 현황 자세히보기
              </Button>
            </Flex>
          </Flex>
        ),
      };
    }
  }, [
    bankRegistered,
    homeTaxRegistered,
    isAnalysisInprogress,
    percent,
    taxInvoicePercent,
    isDemoTrial,
    router,
    labelRatio,
    isOnboardingUser,
  ]);

  return { organizationsInfo, isExistBanner, pendingMandateRequest, bannerHeight, getAlertInfo };
};

export default useGetAlertBannerInfo;

const RegisterTriggerText = ({ text }: { text?: string }) => {
  return (
    <Text
      textStyle="13_600"
      className={css`
        color: ${Colors.gray900};
        cursor: pointer;
        &:hover {
          color: ${PrimaryColors.primary600};
        }
      `}
    >
      {`${text || '연동하기'} →`}
    </Text>
  );
};
